import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useCallback } from 'react';
import { MarketplaceOnly } from '@udemy-v2/react-brand-components';
import { Icon } from '@udemy-v2/icons';
import { Button, IconButton } from '@udemy-v2/react-core-components';
import { useI18n } from '@udemy-v2/i18n';
import { generateTrackingId, publishEvent } from '@udemy-v2/event-tracking';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { useIsLoggedIn } from '@/lib/hooks/use-is-logged-in';
import { GiftBuyablesStartEvent, LearningProduct } from '@/features/course-landing-page/events/events';
import { META_DEFAULT_BASE_URL } from '@/app/[lang]/constants';
import classNames from 'classnames';
import styles from './gift-this-course.module.scss';

/**
 * Extracts course slug from course object
 * @param course - The course object
 * @returns Course slug or course ID as fallback
 */
function extractCourseSlug(course: {
  urlCourseLanding?: string;
  id: string;
}): string {
  if (!course.urlCourseLanding) return course.id;
  try {
    const url = new URL(course.urlCourseLanding);
    const pathParts = url.pathname.split('/').filter(Boolean);
    // URL format: /course/{slug}/ or /course/{slug}
    if (pathParts.length >= 2 && pathParts[0] === 'course') {
      return pathParts[1];
    }
  } catch {
    // If URL parsing fails, fall back to course ID
  }
  return course.id;
}

/**
 * Gift This Course Button Component
 *
 * Renders a button to gift the course to someone else.
 * Only shows for paid courses (not free courses).
 * Wrapped in MarketplaceOnly component to only show on marketplace.
 */
export const GiftThisCourse = t0 => {
  const $ = _c(14);
  const {
    onlyIcon: t1,
    className
  } = t0;
  const onlyIcon = t1 === undefined ? false : t1;
  const {
    gettext,
    locale
  } = useI18n();
  const course = useClpCourse();
  const isLoggedIn = useIsLoggedIn();
  let t2;
  if ($[0] !== course) {
    t2 = extractCourseSlug(course);
    $[0] = course;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const courseSlug = t2;
  let t3;
  if (isLoggedIn) {
    t3 = `/gift/${courseSlug}/`;
  } else {
    let t4;
    if ($[2] !== course.urlCourseLanding || $[3] !== courseSlug || $[4] !== locale) {
      const params = new URLSearchParams({
        locale: locale || "en_US",
        response_type: "html",
        return_link: course.urlCourseLanding || `${META_DEFAULT_BASE_URL}/course/${courseSlug}/`,
        source: "course_landing_page",
        ref: "",
        next: `${META_DEFAULT_BASE_URL}/gift/${courseSlug}/`
      });
      t4 = params.toString();
      $[2] = course.urlCourseLanding;
      $[3] = courseSlug;
      $[4] = locale;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    t3 = `/join/signup-popup/?${t4}`;
  }
  const giftCourseUrl = t3;
  let t4;
  if ($[6] !== course.id) {
    t4 = () => {
      const buyableItem = {
        id: Number(course.id),
        type: LearningProduct.COURSE,
        trackingId: generateTrackingId()
      };
      publishEvent(new GiftBuyablesStartEvent([buyableItem], "sidebar.purchase_section.course_actions"));
    };
    $[6] = course.id;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleGiftClick = t4;
  if (course.isFree) {
    return null;
  }
  let t5;
  if ($[8] !== className || $[9] !== gettext || $[10] !== giftCourseUrl || $[11] !== handleGiftClick || $[12] !== onlyIcon) {
    t5 = <MarketplaceOnly>{onlyIcon ? <IconButton componentClass="a" href={giftCourseUrl} udStyle="ghost" size="large" className={classNames(styles["gift-button"], "ud-link-underline", "ud-link-neutral", className)} data-purpose="gift-this-course" onClick={handleGiftClick} aria-label={gettext("Gift this course")}><Icon label={false} glyph="gift" aria-hidden="true" /></IconButton> : <Button componentClass="a" href={giftCourseUrl} udStyle="ghost" className={classNames(styles["gift-button"], "ud-link-underline", "ud-link-neutral", className)} data-purpose="gift-this-course" onClick={handleGiftClick}>{gettext("Gift this course")}</Button>}</MarketplaceOnly>;
    $[8] = className;
    $[9] = gettext;
    $[10] = giftCourseUrl;
    $[11] = handleGiftClick;
    $[12] = onlyIcon;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};